import { AddCircle } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiResponse } from "src/apis";
import { deleteTax, getTaxes, restoreTax } from "src/apis/TaxApis";
import { GetLocations } from "src/apis/locationApis";
import { useProfile } from "src/auth/Auth";
import Permissions from "src/common_constants/Permission";
import CustomLoader from "src/shared_components/CustomLoader";
import GridPageWrapper from "src/shared_components/GridPageWrapper";
import PopupView from "src/shared_components/PopupView";
import { DataTableColumnProps } from "src/shared_components/Table/DataTable";
import { ActiveOption, FormFieldProps } from "src/shared_components/interfaces";
import TaxManagementForm from "./TaxManagementForm";

interface RateNameManagementGridProps {}

const RateNameManagementGrid: React.FC<RateNameManagementGridProps> = ({}) => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [taxId, setTaxId] = useState<number | null>(null);
  const { clientId } = useProfile();
  const [refreshTable, setRefreshTable] = useState(false);
  const refreshData = () => {
    setRefreshTable((prev) => !prev);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const locationResp: ApiResponse = await GetLocations(`${clientId}`);
        if (locationResp.status === 200) {
          setLocations(locationResp.data.records || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const columns: DataTableColumnProps[] = [
    {
      id: "taxId",
      label: "Id",
      minWidth: 170,
      align: "right",
    },
    {
      id: "name",
      label: t("settings.client.grid.columns.name"),
      minWidth: 130,
      sortable: true,
      isSticky: true,
      isStickyPositionObject: { left: 0 },
    },
    {
      id: "taxNumber",
      label: t("settings.client.grid.columns.taxNumber"),
      minWidth: 130,
      sortable: true,
    },
    {
      id: "defaultTaxValue",
      label: `${t("settings.client.grid.columns.defaultTaxValue")} (%)`,
      minWidth: 130,
      sortable: true,
    },
    {
      id: "location",
      label: t("settings.client.grid.columns.locationName"),
      minWidth: 170,
      sortable: false,
      format: (value) => value ?? "Global",
    },
    {
      id: "description",
      label: t("settings.client.grid.columns.description"),
      minWidth: 170,
      type: "description",
    },
    {
      id: "isDefault",
      label: t("settings.rate.grid.columns.isDefault"),
      minWidth: 100,
      visible: true,
      type: "boolean",
    },
    {
      id: "actionButton",
      label: "",
      minWidth: 100,
      align: "right",
      type: "actions",
      isSticky: true,
      isStickyPositionObject: { right: 0 },
      onEditRow: (row: any) => {
        setOpenForm(true);
        setTaxId(row.taxId);
      },
    },
  ];

  const activeOptions: ActiveOption[] = [
    { value: true, label: t("common.labels.active") },
    { value: false, label: t("common.labels.inActive") },
  ];

  const checkBoxOptions: ActiveOption[] = [
    { value: true, label: t("common.labels.yes") },
    { value: false, label: t("common.labels.no") },
  ];

  const searchFieldProps: FormFieldProps[] = [
    {
      id: "locationId",
      label: t("settings.client.form.inputs.labels.locationId"),
      type: "select",
      options: locations,
      valueKey: "locationId",
      labelKey: "name",
      gridProps: { md: 4, lg: 3, sm: 4, xs: 6 },
      visible: true,
    },
    {
      id: "isActive",
      label: t("asset.form.inputs.labels.active"),
      type: "select",
      options: activeOptions,
      valueKey: "value",
      labelKey: "label",
      gridProps: { md: 3, lg: 2, sm: 4, xs: 6 },
      requirePermission: Permissions.AssetRateDeleteRestore,
      visible: true,
    },
    {
      id: "ApplyToRatePlan",
      label: t("asset.checkBox.appliedToRatePlan"),
      type: "checkBox",
      options: checkBoxOptions,
      valueKey: "value",
      labelKey: "label",
      gridProps: { md: 3, lg: 2, sm: 4, xs: 6 },
      requirePermission: Permissions.AssetRateDeleteRestore,
      visible: true,
    },
    {
      id: "ApplyToAddOns",
      label: t("asset.checkBox.appliedToAddOns"),
      type: "checkBox",
      options: checkBoxOptions,
      valueKey: "value",
      labelKey: "label",
      gridProps: { md: 3, lg: 2, sm: 4, xs: 6 },
      requirePermission: Permissions.AssetRateDeleteRestore,
      visible: true,
    },
  ];

  return (
    <>
      {loading ? (
        <CustomLoader size={30} />
      ) : (
        <GridPageWrapper
          label={t("common.buttons.addNew")}
          icon={<AddCircle />}
          redirectPath={() => setOpenForm(true)}
          heading={t("settings.client.grid.titleTax")}
          columns={columns}
          getData={getTaxes}
          restoreData={restoreTax}
          deleteData={deleteTax}
          searchLabel={t("settings.client.grid.searchTax")}
          permissionCreate={Permissions.AssetRateCreateModify}
          permissionDeleteRestore={Permissions.AssetRateDeleteRestore}
          permissionUpdate={Permissions.AssetRateCreateModify}
          resetData={{ pageNumber: 1 }}
          module="tax"
          disableColumnCustomization={true}
          searchFieldColumns={searchFieldProps}
          disableRowOnClick={true}
          defaultSortBy={"name"}
          isWithIconTitle={true}
          dependency={refreshTable}
        />
      )}
      {openForm && (
        <PopupView
          open={openForm}
          title={
            taxId
              ? t("settings.client.form.titleEditTax")
              : t("settings.client.form.titleAddTax")
          }
          createModifyPermission={Permissions.UserCreate}
          onClose={() => {
            setOpenForm(false);
            setTaxId(null);
          }}
        >
          <TaxManagementForm
            onClose={() => {
              setOpenForm(false);
              setTaxId(null);
            }}
            id={taxId}
            refreshData={() => refreshData()}
          />
        </PopupView>
      )}
    </>
  );
};

export default RateNameManagementGrid;
